import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { createEmployee, getEmployeeByUsername, updateEmployee } from "@/services/users";
import { getMasterDropdownService, getCitiesBasedOnState, getZoneBranch } from "@/services/masters";
import { getBranchByCode } from "@/services/branchIncharge";
import { FormField } from "@/components/ui/FormField";
import Dropdown from "@/components/ui/Dropdown";
import DatePicker from "@/components/ui/DatePicker";
import storage from "@/lib/storage";

interface Branch {
  id: string | number;
  Branch_Name: string;
  unique_code: string;
}

interface Role {
  id: number;
  Name: string;
}

interface Employee {
  id: number;
  Username: string;
  Emp_Name: string;
  Role: string;
  Zone?: string;
  Zone_Code?: string;
  Branch?: string | string[];
  Branch_Code?: string | string[];
  Phone: number;
  Emergency_Contact?: number;
  Address?: string;
  Date_Of_Birth: string;
  Join_Date: string;
  Is_Admin?: boolean;
  Email: string;
  State?: string;
  City?: string;
}

const ADMIN_ROLE = 'Admin';
const ZONE_MANAGER_ROLE = 'Zone Manager';

const employeeSchema = z.object({
  Username: z.string().min(1, "Username is required"),
  Emp_Name: z.string().min(1, "Employee name is required"),
  Role: z.string().min(1, "Role is required"),
  Zone: z.string().optional(),
  Zone_Code: z.string().optional(),
  Branch: z.string().optional(),
  Branch_Code: z.string().optional(),
  BranchPicker: z.string().optional(),

  Phone: z.string().min(10, "Phone must be 10 digits").max(10, "Phone must be 10 digits"),
  Emergency_Contact: z.string().optional().refine(v => !v || v.length === 10, { message: "Emergency contact must be 10 digits" }),
  Address: z.string().optional(),
  Date_Of_Birth: z
    .string()
    .min(1, "Date of Birth is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),

  Join_Date: z
    .string()
    .min(1, "Join Date is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  Is_Admin: z.boolean().optional(),
  Email: z.string().min(1, "Email is required").email("Invalid email address"),
  Password: z.string().optional(),
  Conform_Password: z.string().optional(),
  State: z.string().optional(),
  City: z.string().optional(),
}).superRefine((data, ctx) => {
  if (!data.State)
    ctx.addIssue({ code: "custom", message: "State is required", path: ["State"] });

  if (!data.City)
    ctx.addIssue({ code: "custom", message: "City is required", path: ["City"] });

  // ✅ FIX: Zone only required if NOT Admin
  if (data.Role !== ADMIN_ROLE && !data.Zone) {
    ctx.addIssue({ code: "custom", message: "Zone is required", path: ["Zone"] });
  }

  // ✅ Branch required only for specific roles
  if (
    data.Role &&
    data.Role !== ADMIN_ROLE &&
    data.Role !== ZONE_MANAGER_ROLE &&
    !data.Branch
  ) {
    ctx.addIssue({ code: "custom", message: "Branch is required", path: ["Branch"] });
  }

  if (data.Password && data.Password.length > 0) {
    if (data.Password.length < 6)
      ctx.addIssue({ code: "custom", message: "Password must be at least 6 characters", path: ["Password"] });

    if (!data.Conform_Password)
      ctx.addIssue({ code: "custom", message: "Confirm password is required", path: ["Conform_Password"] });

    if (data.Password !== data.Conform_Password)
      ctx.addIssue({ code: "custom", message: "Passwords do not match", path: ["Conform_Password"] });
  }
});

type EmployeeFormData = z.infer<typeof employeeSchema>;

interface EmployeeModalProps {
  employee: Employee | null;
  onClose: () => void;
  onSuccess: () => void;
  isBranchIncharge?: boolean;
  branchCode?: string;
}

interface MasterDropdownExtended {
  States: { id: number; Name: string; unique_code?: string }[];
  Designations: Role[];
}

export default function EmployeeModal({ employee, onClose, onSuccess, isBranchIncharge, branchCode }: EmployeeModalProps) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [states, setStates] = useState<{ id: number; label: string; value: string }[]>([]);
  const [cities, setCities] = useState<{ id: number; label: string; value: string }[]>([]);
  const [zones, setZones] = useState<{ Zone: string; Zone_Code: string }[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<{ code: string; name: string }[]>([]);

  const [loadingData, setLoadingData] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingZoneBranch, setLoadingZoneBranch] = useState(false);

  const branchLocked = !!isBranchIncharge && !employee;
  const pendingCityRef = useRef<string>("");
  const pendingZoneRef = useRef<string>("");
  const pendingBranchRef = useRef<string>("");
  const pendingSelectedBranchesRef = useRef<{ code: string; name: string }[]>([]);
  const rawStatesRef = useRef<{ id: number; Name: string; unique_code?: string }[]>([]);
  const rawCitiesRef = useRef<any[]>([]);
  const userData = storage.getUserData();

  const { control, handleSubmit, reset, watch, setValue, setError, clearErrors, formState: { isSubmitting, errors } } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      Username: "", Emp_Name: "", Role: "",
      Zone: "", Zone_Code: "", Branch: "", Branch_Code: "", BranchPicker: "",
      Phone: "", Emergency_Contact: "", Address: "",
      Date_Of_Birth: "", Join_Date: "", Is_Admin: false,
      Email: "", Password: "", Conform_Password: "", State: "", City: "",
    },
  });

  const watchedState = watch("State");
  const watchedCity = watch("City");
  const watchedRole = watch("Role");
  const watchedZone = watch("Zone");
  const watchedBranch = watch("Branch");
  const watchedBranchPicker = watch("BranchPicker");

  const isAdmin = watchedRole === ADMIN_ROLE;
  const isZoneManager = watchedRole === ZONE_MANAGER_ROLE;
  const isBranchRequiredRole = !!watchedRole && !isAdmin && !isZoneManager;

  // ── Set Branch_Code when Branch changes ──────────────────────────────────

  useEffect(() => {
  if (!userData) return;

  const isBranchInchargeUser =
    userData.role === "Branch Incharge";

  // Only auto-fill when creating (not editing)
  if (isBranchInchargeUser && !employee) {
    // console.log("Auto filling Branch Incharge data");

    // Set pending refs (important for async dropdown loading)
    pendingCityRef.current = userData.city || "";
    pendingZoneRef.current = userData.zone_code || "";
    pendingBranchRef.current = userData.branch || "";

    // Set immediate values
    setValue("State", userData.state || "");
    setValue("Zone", userData.zone_code || "");

    // Optional: directly set codes if needed
    setValue("Zone_Code", userData.zone_code || "");
    setValue("Branch_Code", userData.branch_code || "");

    // Clear validation errors
    clearErrors(["State", "City", "Zone", "Branch"]);
  }
}, [userData, employee]);

  useEffect(() => {
    if (!watchedBranch) { setValue("Branch_Code", ""); return; }
    const matched = branches.find(b => b.Branch_Name === watchedBranch);
    setValue("Branch_Code", matched?.unique_code ?? "");
  }, [watchedBranch, branches, setValue]);

  // ── Zone Manager: BranchPicker selection → add chip → reset picker ────────
  useEffect(() => {
    if (!watchedBranchPicker) return;
    const branch = branches.find(b => b.unique_code === watchedBranchPicker);
    if (branch && !selectedBranches.find(s => s.code === branch.unique_code)) {
      setSelectedBranches(prev => [...prev, { code: branch.unique_code, name: branch.Branch_Name }]);
    }
    setValue("BranchPicker", "");
  }, [watchedBranchPicker]);

  useEffect(() => {
    if (isAdmin) {
      setValue("Zone", "");
      setValue("Zone_Code", "");
      setValue("Branch", "");
      setValue("Branch_Code", "");
      setSelectedBranches([]);

      clearErrors(["Zone", "Branch"]);
    }
  }, [isAdmin]);

  // ── Load cities when State changes ────────────────────────────────────────
  useEffect(() => {
    if (!watchedState) {
      setCities([]); setBranches([]); setZones([]);
      setValue("City", ""); setValue("Branch", ""); setValue("Zone", ""); setValue("Zone_Code", "");
      pendingCityRef.current = "";
      return;
    }
    if (loadingData) return;
    const load = async () => {
      setLoadingCities(true);
      try {
        const stateCode = rawStatesRef.current.find(s => s.Name === watchedState)?.unique_code || watchedState;
        const res = await getCitiesBasedOnState(stateCode);
        if (res.status === 200 && res.data) {
          const items: any[] = (res.data as any)?.Cities ?? [];
          rawCitiesRef.current = items;
          const list = items.map((c: any, i: number) => ({
            id: typeof c === "object" ? (c.id ?? i) : i,
            label: typeof c === "object" ? c.City_Name : c,
            value: typeof c === "object" ? c.City_Name : c,
          }));
          setCities(list);
          if (pendingCityRef.current) {
            const p = pendingCityRef.current;
            setValue("City", list.find(c => c.label === p || c.value === p)?.value || p);
            pendingCityRef.current = "";
          }
        }
      } finally { setLoadingCities(false); }
    };
    load();
  }, [watchedState, loadingData, setValue]);

  // ── Load only Zones from zone-branch API when City changes ──────────────
  useEffect(() => {
    if (!watchedState || !watchedCity) {
      setBranches([]); setZones([]); setSelectedBranches([]);
      setValue("Branch", ""); setValue("Zone", ""); setValue("Zone_Code", "");
      return;
    }
    const load = async () => {
      setLoadingZoneBranch(true);
      try {
        const stateCode = rawStatesRef.current.find(s => s.Name === watchedState)?.unique_code || watchedState;
        const cityCode = rawCitiesRef.current.find((c: any) => c.City_Name === watchedCity)?.unique_code || watchedCity;
        const res = await getZoneBranch({ State_Code: stateCode, City_Code: cityCode });
        if (res.status === 200 && res.data) {
          setZones(((res.data as any)?.Zones ?? []).map((z: any) => ({ Zone: z.Zone, Zone_Code: z.Zone_Code })));
          if (pendingZoneRef.current) {
            setValue("Zone", pendingZoneRef.current);
            pendingZoneRef.current = "";
          }
        }
      } finally { setLoadingZoneBranch(false); }
    };
    load();
  }, [watchedCity, watchedState, setValue]);

  // ── Load Branches when Zone is selected ──────────────────────────────────
  useEffect(() => {
    if (!watchedZone) {
      setBranches([]); setSelectedBranches([]);
      setValue("Branch", ""); setValue("Branch_Code", "");
      return;
    }
    setValue("Zone_Code", watchedZone);
    const load = async () => {
      setLoadingZoneBranch(true);
      try {
        const res = await getZoneBranch({ Zone_Code: watchedZone });
        if (res.status === 200 && res.data) {
          applyBranchList((res.data as any)?.branches ?? []);
        }
      } finally { setLoadingZoneBranch(false); }
    };
    load();
  }, [watchedZone]);

  function applyBranchList(raw: any[]) {
    const list: Branch[] = raw.map((b: any) => ({
      id: b.Branch_Code ?? b.unique_code,
      Branch_Name: b.Branch_Name,
      unique_code: b.Branch_Code ?? b.unique_code,
    }));
    setBranches(list);
    if (pendingSelectedBranchesRef.current.length > 0) {
      setSelectedBranches(pendingSelectedBranchesRef.current);
      pendingSelectedBranchesRef.current = [];
    } else if (pendingBranchRef.current) {
      setSelectedBranches([]);
      const p = pendingBranchRef.current;
      const matched = list.find(b => b.unique_code === p || b.Branch_Name === p);
      setValue("Branch", matched?.Branch_Name || "");
      pendingBranchRef.current = "";
    } else {
      setSelectedBranches([]);
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      await loadMasterData();
      if (employee) {
        await loadEmployee();
      } else if (branchLocked && branchCode) {
        // try {
        //   const res = await getBranchByCode(branchCode);
        //   const info = Array.isArray(res.data) ? (res.data as any)[0] : res.data as any;
        //   if (info) {
        //     pendingCityRef.current = info.City || "";
        //     pendingBranchRef.current = info.Branch_Name || "";
        //     setValue("Zone", info.Zone || "");
        //     setValue("State", info.State || "");
        //   }
        // } catch { /* silent */ }
      }
    };
    init();
  }, [employee, reset]);

  async function loadEmployee() {
    try {
      const { data } = await getEmployeeByUsername(employee?.Username);
      const e = (data as any)[0] as Employee;
      pendingCityRef.current = e.City || "";
      pendingZoneRef.current = Array.isArray(e.Zone_Code) ? (e.Zone_Code[0] || "") : (e.Zone_Code || "");
      const rawBranch = e.Branch;
      const rawBranchCode = e.Branch_Code;
      if (Array.isArray(rawBranch) && Array.isArray(rawBranchCode)) {
        pendingSelectedBranchesRef.current = rawBranch.map((name: string, i: number) => ({
          name,
          code: (rawBranchCode as string[])[i] || "",
        }));
        pendingBranchRef.current = "";
      } else {
        pendingBranchRef.current = (rawBranch as string) || "";
      }
      reset({
        Username: e.Username, Emp_Name: e.Emp_Name, Role: e.Role,
        Zone: "", Zone_Code: "",
        Branch: "", Branch_Code: "",
        BranchPicker: "",
        Phone: e.Phone?.toString() || "",
        Emergency_Contact: e.Emergency_Contact?.toString() || "",
        Address: e.Address || "", Date_Of_Birth: e.Date_Of_Birth || "",
        Join_Date: e.Join_Date || "", Is_Admin: e.Is_Admin || false,
        Email: e.Email, Password: "", Conform_Password: "",
        State: e.State || "", City: "",
      });
    } catch { /* silent */ }
  }

  const loadMasterData = async () => {
    try {
      const res = await getMasterDropdownService();
      const data = res.data as MasterDropdownExtended;
      const rawStates = data.States || [];
      rawStatesRef.current = rawStates;
      setStates(rawStates.map(s => ({ id: s.id, label: s.Name, value: s.Name })));
      setRoles(data.Designations || []);
    } finally { setLoadingData(false); }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = async (data: EmployeeFormData) => {
    clearErrors();
    try {
      // ✅ Zone Manager validation
      if (isZoneManager && selectedBranches.length === 0) {
        toast.error("Please select at least one branch");
        return;
      }

      const { BranchPicker, ...fields } = data;

      const zoneObj = zones.find(z => z.Zone_Code === data.Zone);
      const zoneName = zoneObj?.Zone ?? data.Zone;
      const zoneCode = data.Zone;

      // ✅ Base payload (common)
      const base: any = {
        ...fields,
        Is_Admin: data.Is_Admin ? 1 : 0,
        Phone: Number(data.Phone),
        Emergency_Contact: data.Emergency_Contact
          ? Number(data.Emergency_Contact)
          : undefined,
      };

      if (!isAdmin) {
        base.Zone = zoneName;
        base.Zone_Code = zoneCode;

        if (isZoneManager) {
          base.Branch = selectedBranches.map(b => b.name);
          base.Branch_Code = selectedBranches.map(b => b.code);
        }
        else if (isBranchIncharge) {
          base.Branch = [userData.branch];
          base.Branch_Code = [userData.branch_code];
        }
        else {
          base.Branch = data.Branch;
          base.Branch_Code = data.Branch_Code;
        }
      }

      let response;

      if (employee) {
        const updatePayload: any = {
          Password: data.Password,
          Conform_Password: data.Conform_Password,
          State: data.State,
          City: data.City,
          Role:data.Role,
        };

        // ✅ Only add Zone & Branch if NOT Admin
        if (!isAdmin) {
          updatePayload.Zone = zoneName;
          updatePayload.Zone_Code = zoneCode;

          if (isZoneManager) {
            updatePayload.Branch = selectedBranches.map(b => b.name);
            updatePayload.Branch_Code = selectedBranches.map(b => b.code);
          } else {
            updatePayload.Branch = data.Branch;
            updatePayload.Branch_Code = data.Branch_Code;
          }
        }

        response = await updateEmployee(employee.id, updatePayload);

      } else {
        // ✅ CREATE
        response = await createEmployee(base);
      }

      // =========================
      // ✅ RESPONSE HANDLING
      // =========================
      if (response.status === 200 || response.status === 201) {
        toast.success(
          employee
            ? "Employee updated successfully"
            : "Employee created successfully"
        );
        onSuccess();
      } else if (response.errors) {
        Object.entries(response.errors).forEach(([key, value]) => {
          const msg = Array.isArray(value) ? value[0] : String(value);

          if (["error", "non_field_errors", "detail"].includes(key)) {
            toast.error(msg);
          } else {
            setError(key as keyof EmployeeFormData, {
              type: "server",
              message: msg,
            });
          }
        });
      } else {
        toast.error(
          (response.data as any)?.message || "Failed to save employee"
        );
      }

    } catch (err: any) {
      toast.error(err.message || "Error saving employee");
    }
  };

  // ── Options ───────────────────────────────────────────────────────────────
  const roleOptions = roles.map(r => ({ id: r.id, label: r.Name, value: r.Name }));
  const zoneOptions = zones.map((z, i) => ({ id: i, label: z.Zone, value: z.Zone_Code }));
  const branchOptions = branches.map(b => ({ id: b.unique_code, label: b.Branch_Name, value: b.Branch_Name }));
  const pickerOptions = branches
    .filter(b => !selectedBranches.find(s => s.code === b.unique_code))
    .map(b => ({ id: b.unique_code, label: b.Branch_Name, value: b.unique_code }));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 rounded-t-xl" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
          <h2 className="text-2xl font-bold text-white">{employee ? "Edit Employee" : "Add New Employee"}</h2>
          <button onClick={onClose} disabled={isSubmitting} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              <FormField control={control} name="Username" label="Username" placeholder="Enter username" disabled={!!employee} required error={errors.Username?.message} />
              <FormField control={control} name="Emp_Name" label="Employee Name" placeholder="Enter employee name" disabled={!!employee} required error={errors.Emp_Name?.message} />
              <FormField control={control} name="Email" label="Email" placeholder="Enter email" type="email" disabled={!!employee} required error={errors.Email?.message} />
              <FormField control={control} name="Phone" label="Phone" placeholder="Enter 10-digit phone" type="tel" disabled={!!employee} required maxLength={10} error={errors.Phone?.message} />
              <FormField control={control} name="Emergency_Contact" label="Emergency Contact" placeholder="Enter 10-digit phone" type="tel" disabled={!!employee} maxLength={10} error={errors.Emergency_Contact?.message} />

              {/* Role */}
              <Dropdown name="Role" control={control} label="Role" options={roleOptions} placeholder="Select role" required isLoading={loadingData} error={errors.Role?.message} />

              {/* State */}
              <Dropdown name="State" disabled={isBranchIncharge} control={control} label="State" options={states} placeholder="Select state" required isLoading={loadingData} error={errors.State?.message} onClear={() => clearErrors("State")} />

              {/* City */}
              <Dropdown name="City" control={control} label="District" options={cities} placeholder={watchedState ? "Select district" : "Select state first"} disabled={!watchedState || isBranchIncharge} required isLoading={loadingCities} error={errors.City?.message} onClear={() => clearErrors("City")} />

              {/* Zone — always shown for all roles */}
              {!isAdmin && (
                <Dropdown
                  name="Zone"
                  control={control}
                  label="Zone"
                  options={zoneOptions}
                  placeholder={watchedCity ? "Select zone" : "Select district first"}
                  disabled={!watchedCity || loadingZoneBranch || isBranchIncharge}
                  isLoading={loadingZoneBranch}
                  required
                  error={errors.Zone?.message}
                />)}

              {/* Branch — single for all roles except Zone Manager */}
              {!isAdmin && !isZoneManager && (
                <Dropdown
                  name="Branch"
                  control={control}
                  label="Branch"
                  options={branchOptions}
                  placeholder={watchedCity ? "Select branch" : "Select district first"}
                  disabled={!watchedCity || loadingZoneBranch || isBranchIncharge}
                  isLoading={loadingZoneBranch}
                  required={isBranchRequiredRole}
                  error={errors.Branch?.message}
                />
              )}

              {/* Branch — multi-select for Zone Manager */}
              {isZoneManager && (
                <div className="space-y-2">
                  <Dropdown
                    name="BranchPicker"
                    control={control}
                    label="Branches *"
                    options={pickerOptions}
                    placeholder={!watchedCity ? "Select district first" : pickerOptions.length === 0 ? "No branches available" : "Add branch…"}
                    disabled={!watchedCity || branches.length === 0}
                    isLoading={loadingZoneBranch}
                  />
                  {selectedBranches.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                      {selectedBranches.map(b => (
                        <span key={b.code} className="inline-flex items-center gap-1.5 px-3 py-1 bg-brand-500 text-white text-xs font-semibold rounded-full">
                          {b.name}
                          <button type="button" onClick={() => setSelectedBranches(prev => prev.filter(x => x.code !== b.code))} className="hover:text-red-300 transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DatePicker name="Date_Of_Birth" required control={control} label="Date of Birth" disabled={!!employee} error={errors.Date_Of_Birth?.message} />
              <DatePicker name="Join_Date" required control={control} label="Join Date" disabled={!!employee} error={errors.Join_Date?.message} />

              <div className="md:col-span-2">
                <FormField control={control} name="Address" label="Address" placeholder="Enter address" isTextarea rows={3} disabled={!!employee} error={errors.Address?.message} />
              </div>

              <FormField control={control} name="Password" label="Password" type="password" placeholder="Enter password" required={!employee} error={errors.Password?.message} />
              <FormField control={control} name="Conform_Password" label="Confirm Password" type="password" placeholder="Confirm password" required={!employee} error={errors.Conform_Password?.message} />

            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-6 border-t border-gray-200 bg-white rounded-b-xl">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-3 rounded-lg transition-all disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="flex-1 bg-mlb-gold hover:bg-mlb-gold/80 text-brand-700 font-semibold py-3 rounded-lg transition-all disabled:opacity-50">
              {isSubmitting ? "Saving..." : employee ? "Update Employee" : "Add Employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
